document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

  function initAgeGate(root = document) {
    const gate = qs('[data-age-gate]', root);
    if (!gate || gate.dataset.initialized) return;
    gate.dataset.initialized = 'true';
    const storageKey = 'bc-cannabis-age-verified';
    const days = Number(gate.dataset.rememberDays || 30);
    let record = null;
    try { record = JSON.parse(localStorage.getItem(storageKey)); } catch (e) {}
    const stillValid = record && record.acceptedAt && (Date.now() - record.acceptedAt) < days * 86400000;
    if (!stillValid) {
      gate.hidden = false;
      document.documentElement.classList.add('age-gate-open');
    }
    qs('[data-age-accept]', gate)?.addEventListener('click', () => {
      try { localStorage.setItem(storageKey, JSON.stringify({ acceptedAt: Date.now() })); } catch (e) {}
      gate.hidden = true;
      document.documentElement.classList.remove('age-gate-open');
    });
    qs('[data-age-decline]', gate)?.addEventListener('click', () => {
      if (gate.dataset.declineUrl) window.location.href = gate.dataset.declineUrl;
    });
  }

  function openLayer(el) {
    if (!el) return;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-open'));
    document.documentElement.classList.add('overlay-open');
  }
  function closeLayer(el) {
    if (!el) return;
    el.classList.remove('is-open');
    window.setTimeout(() => { el.hidden = true; }, 220);
    document.documentElement.classList.remove('overlay-open');
  }

  function initDrawers(root = document) {
    qsa('[data-drawer-open]', root).forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const drawer = document.getElementById(button.dataset.drawerOpen);
        button.setAttribute('aria-expanded', 'true');
        openLayer(drawer);
      });
    });
    qsa('[data-drawer]', root).forEach((drawer) => {
      if (drawer.dataset.initialized) return;
      drawer.dataset.initialized = 'true';
      qsa('[data-drawer-close]', drawer).forEach((button) => button.addEventListener('click', () => closeLayer(drawer)));
      drawer.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeLayer(drawer); });
    });
  }

  function initSearch(root = document) {
    const overlay = qs('[data-search-overlay]');
    qsa('[data-search-open]', root).forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        openLayer(overlay);
        window.setTimeout(() => qs('input[type="search"]', overlay)?.focus(), 120);
      });
    });
    if (overlay && !overlay.dataset.initialized) {
      overlay.dataset.initialized = 'true';
      qsa('[data-search-close]', overlay).forEach((button) => button.addEventListener('click', () => closeLayer(overlay)));
      overlay.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeLayer(overlay); });
    }
  }

  function initFilters(root = document) {
    qsa('[data-filter-toggle]', root).forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        if (!panel) return;
        if (window.matchMedia('(max-width: 899px)').matches) {
          panel.hidden = false;
          requestAnimationFrame(() => panel.classList.toggle('is-open', !expanded));
          document.documentElement.classList.toggle('overlay-open', !expanded);
        } else {
          panel.hidden = expanded;
        }
      });
    });
    qsa('[data-filter-close]', root).forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const panel = button.closest('.filters-panel');
        panel?.classList.remove('is-open');
        document.documentElement.classList.remove('overlay-open');
      });
    });
  }

  function initVariants(root = document) {
    qsa('[data-variant-select]', root).forEach((select) => {
      if (select.dataset.initialized) return;
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const container = select.closest('[data-product-form-wrap]');
        if (!container) return;
        qsa('[data-product-price]', container).forEach((price) => { if (option.dataset.price) price.textContent = option.dataset.price; });
        qsa('[data-product-sku]', container).forEach((sku) => { sku.textContent = option.dataset.sku || ''; });
        const button = qs('[data-add-button]', container);
        if (button) {
          const available = option.dataset.available === 'true';
          button.disabled = !available;
          button.textContent = available ? button.dataset.availableText : button.dataset.soldoutText;
        }
        if (history.replaceState && option.value) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          history.replaceState({}, '', url.toString());
        }
      });
    });
  }

  function initQuantity(root = document) {
    qsa('[data-quantity-stepper]', root).forEach((stepper) => {
      if (stepper.dataset.initialized) return;
      stepper.dataset.initialized = 'true';
      const input = qs('input[type="number"]', stepper);
      qs('[data-quantity-minus]', stepper)?.addEventListener('click', () => {
        if (!input) return;
        input.value = Math.max(Number(input.min || 0), Number(input.value || 0) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      qs('[data-quantity-plus]', stepper)?.addEventListener('click', () => {
        if (!input) return;
        input.value = Number(input.value || 0) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function initQuickAdd(root = document) {
    qsa('[data-quick-add]', root).forEach((form) => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = qs('button[type="submit"]', form);
        if (!button || button.classList.contains('is-loading')) return;
        const originalText = button.textContent;
        button.classList.add('is-loading');
        button.textContent = 'Adding…';
        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST', headers: { 'Accept': 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams(new FormData(form))
          });
          if (!response.ok) throw new Error('Unable to add item');
          const cartResponse = await fetch(`${rootUrl}cart.js`, { headers: { 'Accept': 'application/json' } });
          const cart = await cartResponse.json();
          qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
          button.classList.remove('is-loading');
          button.classList.add('is-added');
          button.textContent = 'Added';
          window.setTimeout(() => { button.classList.remove('is-added'); button.textContent = originalText; }, 1300);
        } catch (error) {
          button.classList.remove('is-loading');
          button.textContent = 'Try again';
          window.setTimeout(() => { button.textContent = originalText; }, 1600);
        }
      });
    });
  }

  function initGallery(root = document) {
    const product = qs('[data-product-section]', root) || (root.matches?.('[data-product-section]') ? root : null);
    if (!product || product.dataset.galleryInitialized) return;
    product.dataset.galleryInitialized = 'true';
    qsa('[data-gallery-thumb]', product).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const id = thumb.dataset.galleryThumb;
        qsa('[data-gallery-thumb]', product).forEach((item) => item.classList.toggle('is-active', item === thumb));
        qsa('[data-gallery-media]', product).forEach((media) => {
          const active = media.dataset.galleryMedia === id;
          media.hidden = !active;
          media.classList.toggle('is-active', active);
        });
      });
    });
    const dialog = qs('[data-gallery-dialog]');
    qsa('[data-gallery-zoom]', product).forEach((button) => button.addEventListener('click', () => {
      const image = qs('[data-gallery-dialog-image]', dialog);
      if (image) image.src = button.dataset.galleryZoom;
      if (dialog?.showModal) dialog.showModal();
    }));
    qs('[data-gallery-dialog-close]', dialog)?.addEventListener('click', () => dialog.close());
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
  }

  async function initRecommendations(root = document) {
    const section = qs('[data-product-recommendations]', root) || (root.matches?.('[data-product-recommendations]') ? root : null);
    if (!section || section.dataset.loaded || !section.dataset.url) return;
    section.dataset.loaded = 'true';
    try {
      const response = await fetch(section.dataset.url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const replacement = qs('[data-product-recommendations]', doc);
      if (replacement?.innerHTML.trim()) {
        section.innerHTML = replacement.innerHTML;
        initAll(section);
      }
    } catch (e) {}
  }

  function initReveal(root = document) {
    const items = qsa('[data-reveal]', root);
    if (!items.length) return;
    document.documentElement.classList.add('reveal-ready');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -35px 0px' });
    items.forEach((item) => { if (!item.classList.contains('is-revealed')) observer.observe(item); });
  }

  function initHeaderScroll() {
    const header = qs('[data-site-header]');
    if (!header || header.dataset.scrollInitialized) return;
    header.dataset.scrollInitialized = 'true';
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function initAll(root = document) {
    initAgeGate(root); initDrawers(root); initSearch(root); initFilters(root); initVariants(root); initQuantity(root); initQuickAdd(root); initGallery(root); initReveal(root); initRecommendations(root); initHeaderScroll();
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
